import React, { useState } from "react";
import styled from "styled-components/macro";
import { ResourceStrings } from "../../ResourceStrings";
import { AppPaths } from "../../constants/urls";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { UrlParam } from "../../constants/urlParams";
import { CommandBarButton } from "@fluentui/react/lib/Button";
import { IContextualMenuItem } from "@fluentui/react/lib/ContextualMenu";
import { cssVarStr } from "../../utils/react";
import { darken } from "polished";
import { forMinWidth } from "../../theme/breakpoints";
import { INavLink, INavLinkGroup, INavStyles, Nav } from "@fluentui/react/lib/Nav";
import { History } from "history";
import { SpaceConfig } from "../../config/spaces/spaceConfig";
import { App } from "../../app";
import { getPotentialSlugFromPathName, updateUrlParams } from "../../utils/webPage";
import { useSelector } from "react-redux";
import { RootState } from "../../store/rootState";
import { Permission, UserRole } from "../../config/permissions";
import { hasPermission } from "../../config/accessPolicies";
import { SpaceConfigGroup, SpaceItem, getApplicableSpaces, getSlugFromSpaceId } from "../../config/serverConfig";

const paramsToExcludeInNavBarLinks = [UrlParam.ProvideMoreInfo, UrlParam.DetailViewType];

interface LinkInfo {
    key: string;
    path: string;
    text: string;
}

const linksData = [
    { text: ResourceStrings.myFeedback(), path: AppPaths.myfeedback },
    { text: ResourceStrings.myActivity(), path: AppPaths.myactivity },
];

const links: LinkInfo[] = linksData.map(l => ({ ...l, key: l.path } as LinkInfo));

enum GroupKeys {
    explore = "explore",
}

export default function NavBar(): JSX.Element {
    const history: History<unknown> = useHistory();
    const match = useRouteMatch("/:path");
    const location = useLocation();

    const [isExpanded, setIsExpanded] = useState({
        [GroupKeys.explore]: true,
    } as { [key: string]: boolean });

    const userRoles: UserRole[] = useSelector((state: RootState) => state.appState.userRoles);
    const isInPreview2: boolean = App.previewFeaturesEnabled || hasPermission(Permission.Preview2, userRoles);

    const getParamsForLink = (link: LinkInfo): URLSearchParams => {
        const params = new URLSearchParams(history.location.search);
        paramsToExcludeInNavBarLinks.forEach(x => params.delete(x));
        return params;
    }

    const getSelectedLink = (currentPath: string, links: LinkInfo[]): LinkInfo => links.find(link => {
        const trimmedPath = link.path.replace(/^\//g, '');
        const isSelected = trimmedPath.toLowerCase() === currentPath?.toLowerCase();
        return isSelected;
    });

    const selectedLink: LinkInfo = getSelectedLink(match?.params["path"], links);

    const menuItems: IContextualMenuItem[] = links
        .map((l: LinkInfo) => ({
            ...l,
            key: `nav-mi-${l.key}`,
            role: "link",
            // eslint-disable-next-line react/display-name
            onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void): React.ReactNode => {
                const link = item as LinkInfo;
                const params = getParamsForLink(link);
                const isSelected = selectedLink && link.path === selectedLink.path;

                return <MenuItem
                    key={`nav-mi-${link.key}`}
                    $isActive={isSelected}
                    role="link"
                    onClick={(ev) => dismissMenu(ev, true)}
                    to={`${link.path}?${params}`}
                >
                    {link.text}
                </MenuItem>
            }
        }));
    menuItems.push({
        key: "nav-devcom2",
        role: "link",
        href: AppPaths.home,
        // eslint-disable-next-line react/display-name
        onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void): React.ReactNode => {
            return <a href={AppPaths.home} key="nav-devcom2" target="_blank" rel="noopener noreferrer" css="padding: 1rem; min-height: 2rem;">
                Explore Feedback&nbsp;&gt;
            </a>;
        }
    });

    if (!isInPreview2) {
        return <Wrapper>
            <Nav1 hasASelectedItem={!!selectedLink}>
                <CommandBarButton
                    className="menu-button"
                    menuProps={{
                        items: menuItems
                    }}
                >
                    {selectedLink ? selectedLink.text : ResourceStrings.selectAPage()}
                </CommandBarButton>

                <span role="tablist" className="tab-list">
                    {links.map((link: LinkInfo) => {
                        const params = getParamsForLink(link);
                        const isSelected = link === selectedLink;

                        return <NavItem key={link.key} $isActive={isSelected}>
                            <Link
                                role="tab"
                                aria-selected={isSelected ? "true" : "false"}
                                to={`${link.path}?${params}`}
                            >
                                {link.text}
                            </Link>
                        </NavItem>
                    })}
                    <NavItem key={"exploreFeedback"}>
                        <a href={AppPaths.home} target="_blank" rel="noopener noreferrer">
                            Explore Feedback&nbsp;&gt;
                        </a>
                    </NavItem>
                </span>
            </Nav1>
        </Wrapper>
    }

    /** figures out what to select in the NavBar for a given URL */
    const getSelectedKey = (): string => {
        const path: string = location.pathname;

        // for paths like /VisualStudio/report or /.../suggest the key is the path itself
        // nikolami: 2022-10-14: this will not work with /VisualStudio/report/somerandomstuffhere, by current and past design.
        if ((path.indexOf(AppPaths.report) >= 0) || (path.indexOf(AppPaths.suggest) >= 0)) {
            return path.toLowerCase();
        }

        // for paths like /VisualStudio or /VisualStudio/whatever/something else (but not .../report or .../suggest) -> return just the /slug part of the path
        return `/${(getPotentialSlugFromPathName(path) ?? "").toLowerCase()}`
    };

    const isExpandedUpdates: { [groupKey: string]: boolean } = {};
    const navStyles: Partial<INavStyles> = {
        groupContent: {
            marginBottom: "0rem",
        },
        root: {
            minWidth: "24rem",
            background: "var(--color-surface)",
            height: "100%",
            borderRight: "0.1rem solid var(--color-selected-background)",
        },
        link: {
            "::after": {
                borderLeft: "none",
            },
        },
        chevronButton: { // this is the group header in nav
            fontSize: "inherit",
            margin: "0",
            paddingLeft: "3rem",
            color: "var(--color-on-surface)",
            borderBottom: "none",
        }
    };

    const getExpandHandler = (key: GroupKeys | string) => {
        return (ev, isCollapsing) => {
            const isExpandedUpdate = {};
            isExpandedUpdate[key] = !isCollapsing;
            setIsExpanded({ ...isExpanded, ...isExpandedUpdate });
        };
    };

    const validSpaces: SpaceItem[] = getApplicableSpaces(userRoles);
    const urlParams: URLSearchParams = new URLSearchParams(location.search);
    const initialUrlParams: string = urlParams.toString();

    /** Returns a nav link for a given space or space group */
    const getLinkFromSpace = (item: SpaceItem, keySuffix: string, groupKey: GroupKeys | string, childGroupKey?: string): INavLinkGroup => {
        updateUrlParams(urlParams, { [UrlParam.PageNumber]: null });

        let key: string;
        let result: INavLinkGroup;

        // if space group
        if ((item as SpaceConfigGroup).spaces) {
            const group: SpaceConfigGroup = item as SpaceConfigGroup;
            const childGroupFullPathKey: string = `${groupKey}_${group.key}`; // e.g. "explore__liftr"
            key = `/${group.key.toLowerCase()}${keySuffix}`;

            result = {
                name: group.displayName,
                expandAriaLabel: `${group.displayName}`,
                collapseAriaLabel: `${group.displayName} `,
                isExpanded: isExpanded[childGroupFullPathKey] ?? false,
                onHeaderClick: getExpandHandler(childGroupFullPathKey),
                links: group.spaces.map((groupSpace: SpaceConfig) => {
                    const link: INavLink = getLinkFromSpace(groupSpace, keySuffix, groupKey, childGroupFullPathKey).links[0];
                    link.isChild = true;
                    return link;
                }),
            };
        } // .. else space
        else {
            const space: SpaceConfig = item as SpaceConfig;
            const url = `/${space.slug}${keySuffix}?${urlParams}`;
            key = `/${space.slug.toLowerCase()}${keySuffix}`;

            result = {
                links: [{
                    name: space.displayName,
                    key,
                    url,

                    // fix Narrator not supporting `aria-current` property.
                    // See bug https://dev.azure.com/devdiv/DevDiv/_workitems/edit/1532847/ and https://a11ysupport.io/tech/aria/aria-current_attribute
                    // ariaLabel line can be removed once Narrator supports `aria-current` property
                    ariaLabel: selectedKey === key ? `${space.displayName}: selected` : space.displayName,
                }]
            };
        }

        // if item is selected, expand all groups it belongs to
        if (selectedKey === key) {
            // expand root group if needed
            if (!isExpanded[groupKey]) {
                isExpandedUpdates[groupKey] = true;
                mustRefreshIsExpanded = true;
            }

            // expand child group (if any) under root if needed
            if (childGroupKey && !isExpanded[childGroupKey]) {
                isExpandedUpdates[childGroupKey] = true;
                mustRefreshIsExpanded = true;
            }
        }

        return result;
    };

    const selectedKey: string = getSelectedKey();
    let mustRefreshIsExpanded: boolean = false;
    const exploreLinks: INavLinkGroup[] = validSpaces.map((item: SpaceItem): INavLinkGroup => {
        return getLinkFromSpace(item, "", GroupKeys.explore);
    });

    /** Returns a function that will recursively filter SpaceConfig | SpaceGroupConfig */
    const getFilterSpaceOrGroup = (filterFunc: (space: SpaceConfig) => boolean): (item: SpaceItem) => boolean => {
        return (item: SpaceItem): boolean => {
            // if item is a group
            if ((item as SpaceConfigGroup).spaces) {
                const group: SpaceConfigGroup = item as SpaceConfigGroup;
                return group.spaces.some(filterFunc);
            }
            else {
                // ... else item is a space
                const space: SpaceConfig = item as SpaceConfig;
                return filterFunc(space);
            }
        };
    };

    const filterSpaces = (items: SpaceItem[], filterFunc: (space: SpaceConfig) => boolean): SpaceItem[] => {
        const result: SpaceItem[] = [];
        items.forEach((item: SpaceItem) => {
            // if item is a group
            if ((item as SpaceConfigGroup).spaces) {
                const group: SpaceConfigGroup = item as SpaceConfigGroup;
                const someVisible: boolean = group.spaces.some(filterFunc);
                if (someVisible) {
                    const allVisible: boolean = group.spaces.every(filterFunc);
                    if (allVisible) {
                        result.push(group);
                    }
                    else {
                        // some but not all are visible - copy the group with the spaces that don't match the filter removed
                        const newGroup: SpaceConfigGroup = { ...group };
                        newGroup.spaces = group.spaces.filter(filterFunc);
                        result.push(newGroup);
                    }
                }
            }
            else {
                // ... else item is a space
                const space: SpaceConfig = item as SpaceConfig;
                if (filterFunc(space)) {
                    result.push(space);
                }
            }
        });
        return result;
    };

    if (mustRefreshIsExpanded) {
        setIsExpanded({ ...isExpanded, ...isExpandedUpdates });
    }

    const navLinkGroups: INavLinkGroup[] = [
        {
            links: [
                {
                    name: 'Home',
                    key: AppPaths.home,
                    url: `${AppPaths.home}?${initialUrlParams}`,
                }
            ],
        },
        ...exploreLinks,
    ];

    const selectedContextMenuLinkKey: string = `nav-mi2-${selectedKey}`;
    const contextMenuItemsFromLinkGroups = (linkGroups: INavLinkGroup[]): IContextualMenuItem[] => {
        const menuItems: IContextualMenuItem[] = [];
        for (const linkGroup of linkGroups) {
            if (linkGroup.name) {
                const groupItem: IContextualMenuItem = {
                    key: `nav-mi2-${linkGroup.name}`,
                    subMenuProps: {
                        items: contextMenuItemsFromLinks(linkGroup.links)
                    },
                    text: linkGroup.name,
                };
                menuItems.push(groupItem);
            }
            else {
                menuItems.push(contextMenuItemsFromLinks(linkGroup.links)[0]);
            }
        }
        return menuItems;
    }
    const contextMenuItemsFromLinks = (links: INavLink[]): IContextualMenuItem[] => {
        return links.map((l: INavLink) => {
            if (l.links) { // if sub-group of links
                return {
                    key: `nav-mi2-${l.key}`,
                    subMenuProps: {
                        items: contextMenuItemsFromLinks(l.links)
                    },
                    text: l.name,
                };
            }

            return {
                href: l.url,
                key: `nav-mi2-${l.key}`,
                role: "link",
                text: l.name,
                path: l.path,
                // eslint-disable-next-line react/display-name
                onRender: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void): React.ReactNode => {
                    const link = item as INavLink;
                    const isSelected = link.key === selectedContextMenuLinkKey;

                    return <MenuItem
                        key={`nav-mi2-${link.key}`}
                        $isActive={isSelected}
                        role="link"
                        onClick={(ev) => dismissMenu(ev, true)}
                        to={link.href}
                    >
                        {link.text}
                    </MenuItem>
                }
            };
        });
    };

    // gets currently selected path name e.g. "Report Problem > Visual Studio"
    const getSelectedPathText = (selectedKey: string, linkGroups: INavLinkGroup[]): string | null => {
        for (const linkGroup of linkGroups) {
            const potentialPath: string | null = linkGroup.name;
            for (const link of linkGroup.links) {
                if (link.key === selectedKey) {
                    return potentialPath ? potentialPath + ` > ${link.name}` : link.name;
                }

                // if sub-group of links
                if (link.links) {
                    // find path in sub-group
                    for (const childLink of link.links) {
                        if (childLink.key === selectedKey) {
                            return potentialPath ? potentialPath + ` > ${link.name} > ${childLink.name}` : childLink.name;
                        }
                    }
                }
            }
        }

        return null;
    };

    const selectedPathText: string | null = getSelectedPathText(selectedKey, navLinkGroups);
    const menuItems2: IContextualMenuItem[] = contextMenuItemsFromLinkGroups(navLinkGroups);
    return (
        <Nav2Wrapper>
            <div css="display:flex">
                <CommandBarButton className="menu-button" menuProps={{ items: menuItems2 }}>
                    {selectedPathText ?? ResourceStrings.selectAPage()}
                </CommandBarButton>
                <Nav className="tab-list" styles={navStyles} ariaLabel="Spaces" groups={navLinkGroups}
                    onLinkClick={(ev: React.MouseEvent<HTMLElement>, item: INavLink) => {
                        if (!item.url) { // if group header
                            return;
                        }
                        if (ev) {
                            ev.nativeEvent.preventDefault();
                        }
                        if (history?.push) {
                            history.push(item.url);
                        }
                    }}
                    onLinkExpandClick={(ev: React.MouseEvent<HTMLElement>, item: INavLink) => {
                        const updates = {};
                        updates[item.key] = !item.isExpanded;
                        setIsExpanded({ ...isExpanded, ...updates });
                    }}
                    selectedKey={selectedKey}
                    onRenderLink={(link: INavLink) => {
                        if (link.isChild) {
                            return <div css="padding-left: 2rem;">{link.name}</div>;
                        }
                        else {
                            return <div>{link.name}</div>;
                        }
                    }}
                />
            </div>
        </Nav2Wrapper>
    );
}

const Nav2Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    a, a * {
        color: var(--color-on-surface);
    }

    .menu-button {
        display: flex;
        height: 5rem;
        font-size: 1.4rem;
        line-height: 2.1rem;
        padding: 1rem;

        .ms-Button-label {
            margin-right: 2rem;
        }
    }

    .tab-list {
        display: none;
        margin-bottom: 0;
    }

    ${forMinWidth.lg`
        flex-direction: row;

        .tab-list {
            display: inherit;
        }

        .menu-button {
            display: none;
        }
    `}
`;

const Wrapper = styled.div`
    background-color: var(--color-surface);
    width: 100%;
    display: flex;
    justify-content: center;
    flex: 0 0 0;
    min-width: 22rem;
    border-right: 0.1rem solid var(--color-selected-background);
`;

const Nav1 = styled.nav<{ hasASelectedItem?: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;

    >* {
        display: flex;

        &:first-child a {
            color: var(--color-on-surface);
        }
    }

    .tab-list {
        display: none;
        flex-direction: column;
    }

    .menu-button {
        display: flex;
        height: 5rem;
        font-size: 1.4rem;
        line-height: 2.1rem;
        padding: 1rem;

        border-bottom: ${props => props.hasASelectedItem ? "solid  2px var(--color-secondary)" : "none"};

        .ms-Button-label {
            font-weight: ${props => props.hasASelectedItem ? "600" : "normal"};
            margin-right: 2rem;
        }
    }

    ${forMinWidth.lg`
        .tab-list {
            display: inherit;

        }

        .menu-button {
            display: none;
        }
    `}
`;

const NavItem = styled.span<{ $isActive?: boolean }>`
    display: flex;
    align-items: center;

    height: 5rem;
    font-size: 1.4rem;
    line-height: 2.1rem;
    text-align: center;

    font-weight: ${props => props.$isActive ? "600" : "normal"};
    background: ${props => props.$isActive ? "var(--color-selected-background)" : "none"};

    a {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        padding: 0.2rem 1.4rem;
        color: var(--color-text);
    }
`;

const MenuItem = styled(Link) <{ $isActive?: boolean }>`
    display: block;
    padding: .5rem 0.8rem;
    margin: 0.3rem;

    font-size: 1.6rem;
    line-height: 2.5rem;

    font-weight: ${props => props.$isActive ? "600" : "normal"};
    color: ${props => props.$isActive ? "var(--color-secondary)" : "var(--color-on-surface-link)"};

    &:hover, &:focus {
        background-color: ${darken(0.05, cssVarStr("--color-surface"))}
    }
`;
